import { create } from "zustand";
import type { RaceOver, RaceState } from "@mk/shared";
import { getSocket } from "../../lib/socket.ts";
import { useSession } from "../../lib/session.ts";

type Phase = "idle" | "countdown" | "live" | "over";
interface RaceStore {
  phase: Phase;
  state: RaceState | null;
  result: RaceOver | null;
  setState: (state: RaceState) => void;
  setResult: (result: RaceOver) => void;
  setVotes: (matchId: string, votes: number) => void;
  reset: () => void;
}

export const useRaceStore = create<RaceStore>((set) => ({
  phase: "idle", state: null, result: null,
  setState: (state) => set((current) => {
    if (current.phase === "over" && current.result?.matchId === state.matchId) return {};
    if (current.state && current.state.matchId !== state.matchId && current.state.startsAt > state.startsAt) return {};
    return { state, result: null, phase: Date.now() < state.startsAt ? "countdown" : "live" };
  }),
  setResult: (result) => set((current) => current.state?.matchId === result.matchId
    ? { result, phase: "over" } : {}),
  setVotes: (matchId, votes) => set((current) => current.result?.matchId === matchId
    ? { result: { ...current.result, rematchVotes: votes } } : {}),
  reset: () => set({ phase: "idle", state: null, result: null }),
}));

let detach: (() => void) | null = null;
export function attachRaceListeners(): () => void {
  if (detach) return detach;
  const socket = getSocket();
  const onState = (state: RaceState) => useRaceStore.getState().setState(state);
  const onOver = (result: RaceOver) => {
    useRaceStore.getState().setResult(result);
    void useSession.getState().refreshStats();
  };
  const onVotes = ({ matchId, votes }: { matchId: string; votes: number }) =>
    useRaceStore.getState().setVotes(matchId, votes);
  socket.on("race:found", onState);
  socket.on("race:state", onState);
  socket.on("race:over", onOver);
  socket.on("race:rematch:votes", onVotes);
  detach = () => {
    socket.off("race:found", onState);
    socket.off("race:state", onState);
    socket.off("race:over", onOver);
    socket.off("race:rematch:votes", onVotes);
    detach = null;
  };
  return detach;
}
